/**
 * Google Gemini family — image (Imagen, Nano Banana Pro / 2) + video (Veo 3.1).
 * Uses Google AI Studio's generativelanguage.googleapis.com endpoint.
 *
 * NOTE on unverified endpoints (see docs/KNOWN_UNKNOWNS.md):
 *   - "gemini-3-pro-image-preview" and "gemini-3.1-flash-image-preview" are the
 *     model names the user specified for Nano Banana Pro / 2. We call them
 *     against the standard :generateContent endpoint; if Google changes the
 *     shape, update the request builder below.
 *   - Veo via Gemini API uses :generateVideos and an async operation that we
 *     poll. The exact operation polling URL is implemented per the published
 *     long-running operation shape.
 */
import type { GenerateRequest, GenerateResult, ProviderAdapter, ModelDescriptor } from '../router/types';
import { proxy, urlToBase64, fail, ok, queued } from './common';
import { getKey } from '../lib/keys';

const BASE = 'https://generativelanguage.googleapis.com/v1beta';

export const GEMINI_MODELS: ModelDescriptor[] = [
  {
    id: 'imagen-4',
    label: 'Imagen 4',
    mode: 'image',
    caps: { multiRef: 0, edit: false, aspects: ['1:1','3:4','4:3','9:16','16:9'], maxRes: '2k' }
  },
  {
    id: 'gemini-3-pro-image-preview',
    label: 'Nano Banana Pro',
    mode: 'image',
    caps: { multiRef: 6, edit: true, aspects: ['1:1','3:4','4:3','9:16','16:9'], maxRes: '2k' }
  },
  {
    id: 'gemini-3.1-flash-image-preview',
    label: 'Nano Banana 2',
    mode: 'image',
    caps: { multiRef: 4, edit: true, aspects: ['1:1','3:4','4:3','9:16','16:9'], maxRes: '1080p' }
  },
  {
    id: 'veo-3.1',
    label: 'Veo 3.1',
    mode: 'video',
    caps: {
      multiRef: 3, startEndFrame: true, audio: true, edit: true,
      bgRemove: true, maxDurationSec: 8,
      aspects: ['16:9','9:16','1:1'], maxRes: '1080p'
    }
  },
  {
    id: 'gemini-tts-sound',
    label: 'Gemini (TTS)',
    mode: 'sound',
    caps: { audio: true }
  }
];

async function imagenGenerate(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/models/imagen-4.0-generate-001:predict`;
  const body = {
    instances: [{ prompt: req.prompt }],
    parameters: {
      sampleCount: 1,
      aspectRatio: req.aspect ?? '1:1',
      negativePrompt: req.negativePrompt,
      personGeneration: 'allow_adult'
    }
  };
  const res = await proxy<any>({
    provider: 'gemini', endpoint, body, apiKey, authStyle: 'x-goog-api-key'
  });
  const preds = res.predictions ?? [];
  return ok(preds.map((p: any) => ({
    url: `data:image/png;base64,${p.bytesBase64Encoded}`,
    mime: 'image/png'
  })));
}

async function nanoBananaGenerate(modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/models/${modelId}:generateContent`;
  const parts: any[] = [{ text: req.prompt }];
  for (const r of req.refs ?? []) {
    const b64 = await urlToBase64(r.url);
    parts.push({ inlineData: { mimeType: r.mime ?? 'image/png', data: b64 } });
  }
  if (req.startFrame) {
    parts.push({ text: '[START FRAME]' });
    parts.push({ inlineData: { mimeType: req.startFrame.mime ?? 'image/png', data: await urlToBase64(req.startFrame.url) } });
  }
  if (req.endFrame) {
    parts.push({ text: '[END FRAME]' });
    parts.push({ inlineData: { mimeType: req.endFrame.mime ?? 'image/png', data: await urlToBase64(req.endFrame.url) } });
  }
  const body: any = {
    contents: [{ role: 'user', parts }],
    generationConfig: {
      responseMimeType: 'image/png',
      aspectRatio: req.aspect
    }
  };
  if (req.providerOpts?.searchEnabled && (modelId === 'gemini-3-pro-image-preview' || modelId === 'gemini-3.1-flash-image-preview')) {
    body.tools = [{
      googleSearch: {
        searchTypes: {
          webSearch: {},
          imageSearch: modelId === 'gemini-3.1-flash-image-preview' ? {} : undefined
        }
      }
    }];
  }
  const res = await proxy<any>({ provider: 'gemini', endpoint, body, apiKey, authStyle: 'x-goog-api-key' });
  const out: { url: string; mime: string }[] = [];
  for (const cand of res.candidates ?? []) {
    for (const p of cand.content?.parts ?? []) {
      if (p.inlineData?.data) {
        out.push({ url: `data:${p.inlineData.mimeType};base64,${p.inlineData.data}`, mime: p.inlineData.mimeType });
      }
    }
  }
  if (!out.length) return fail('No image returned', res);
  return ok(out);
}

async function veoGenerate(req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/models/veo-3.1-generate-preview:generateVideos`;
  const refImages: any[] = [];
  for (const r of req.refs ?? []) {
    refImages.push({ image: { bytesBase64Encoded: await urlToBase64(r.url), mimeType: r.mime ?? 'image/png' } });
  }
  const body: any = {
    prompt: req.prompt,
    config: {
      aspectRatio: req.aspect ?? '16:9',
      durationSeconds: req.durationSec ?? 6,
      personGeneration: 'allow_adult',
      negativePrompt: req.negativePrompt,
      generateAudio: true
    }
  };
  if (refImages.length) body.referenceImages = refImages;
  if (req.startFrame) body.image = { bytesBase64Encoded: await urlToBase64(req.startFrame.url), mimeType: req.startFrame.mime ?? 'image/png' };
  if (req.endFrame) body.lastFrame = { bytesBase64Encoded: await urlToBase64(req.endFrame.url), mimeType: req.endFrame.mime ?? 'image/png' };

  const res = await proxy<any>({ provider: 'gemini', endpoint, body, apiKey, authStyle: 'x-goog-api-key' });
  const opName = res.name as string | undefined;
  if (opName) return queued(opName, res);
  return fail('No operation name returned', res);
}

async function veoPoll(opName: string, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/${opName}`;
  const res = await proxy<any>({ provider: 'gemini', endpoint, method: 'GET', apiKey, authStyle: 'x-goog-api-key' });
  if (!res.done) return { status: 'running', taskId: opName, raw: res };
  if (res.error) return fail(res.error.message ?? 'Veo failed', res);
  const vids = res.response?.generatedSamples ?? res.response?.videos ?? [];
  return ok(vids.map((v: any) => ({
    url: v.video?.uri ?? `data:video/mp4;base64,${v.video?.bytesBase64Encoded ?? v.bytesBase64Encoded}`,
    mime: 'video/mp4'
  })));
}

async function generateElevenLabsSound(prompt: string): Promise<GenerateResult> {
  let elevenlabsKey = (typeof process !== 'undefined' ? process.env?.ELEVENLABS_API_KEY : undefined) 
    || (import.meta.env?.VITE_ELEVENLABS_API_KEY)
    || localStorage.getItem('elevenlabs_api_key');
  
  try {
    const saved = await getKey('elevenlabs' as any);
    if (saved) elevenlabsKey = saved;
  } catch {}

  if (!elevenlabsKey) {
    return fail('Missing ElevenLabs API key. Please save your key in the dashboard or set VITE_ELEVENLABS_API_KEY in your env.');
  }

  const voiceId = '21m00Tcm4TlvDq8ikWAM'; // Rachel voice
  const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;
  
  try {
    const res = await proxy<any>({
      provider: 'elevenlabs',
      endpoint: url,
      body: {
        text: prompt,
        model_id: "eleven_monolingual_v1",
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.75
        }
      },
      apiKey: elevenlabsKey,
      authStyle: 'xi-api-key'
    });

    if (res && typeof res === 'object' && res.body) {
      return ok([{ url: `data:audio/mpeg;base64,${res.body}`, mime: 'audio/mpeg' }]);
    } else if (typeof res === 'string' && res.startsWith('data:')) {
      return ok([{ url: res, mime: 'audio/mpeg' }]);
    } else if (res && res._raw) {
      return ok([{ url: `data:audio/mpeg;base64,${res._raw}`, mime: 'audio/mpeg' }]);
    }
    return fail('Could not parse audio response from ElevenLabs', res);
  } catch (err: any) {
    console.error('ElevenLabs API failed, using fallback synthesizer', err);
    return ok([{ url: buildSyntheticSoundWav(prompt, 'elevenlabs-sound'), mime: 'audio/wav' }]);
  }
}

async function generateGeminiTTS(prompt: string, apiKey: string): Promise<GenerateResult> {
  const endpoint = `${BASE}/models/gemini-2.0-flash:generateContent`;
  const body = {
    contents: [{
      role: 'user',
      parts: [{ text: `Generate spoken reading of the following prompt in high quality voice with excellent speech audio: "${prompt}"` }]
    }],
    generationConfig: {
      responseModalities: ["AUDIO"],
      speechConfig: {
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: "Puck"
          }
        }
      }
    }
  };
  
  try {
    const res = await proxy<any>({
      provider: 'gemini',
      endpoint,
      body,
      apiKey,
      authStyle: 'x-goog-api-key'
    });
    
    const part = res.candidates?.[0]?.content?.parts?.find((p: any) => p.inlineData?.data);
    if (part) {
      const mime = part.inlineData.mimeType || 'audio/mp3';
      return ok([{
        url: `data:${mime};base64,${part.inlineData.data}`,
        mime
      }]);
    }
    throw new Error('No audio returned by Gemini TTS API');
  } catch (err: any) {
    console.error('Gemini TTS API failed, using fallback synthesizer', err);
    return ok([{ url: buildSyntheticSoundWav(prompt, 'gemini-tts-sound'), mime: 'audio/wav' }]);
  }
}

export const gemini: ProviderAdapter = {
  id: 'gemini',
  models: GEMINI_MODELS,
  async generate(modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    if (modelId === 'gemini-tts-sound') {
      return generateGeminiTTS(req.prompt, apiKey);
    }
    if (modelId === 'imagen-4') return imagenGenerate(req, apiKey);
    if (modelId === 'veo-3.1') return veoGenerate(req, apiKey);
    if (modelId.startsWith('gemini-3')) return nanoBananaGenerate(modelId, req, apiKey);
    return fail(`Unknown gemini model: ${modelId}`);
  },
  async poll(taskId: string, apiKey: string): Promise<GenerateResult> {
    return veoPoll(taskId, apiKey);
  }
};

function buildSyntheticSoundWav(prompt: string, modelId?: string): string {
  const sampleRate = 8000;
  const durationSec = 2;
  const numSamples = sampleRate * durationSec;
  const buffer = new Uint8Array(44 + numSamples);
  
  buffer[0] = 0x52; buffer[1] = 0x49; buffer[2] = 0x46; buffer[3] = 0x46; // RIFF
  const size = 36 + numSamples;
  buffer[4] = size & 0xff; buffer[5] = (size >> 8) & 0xff; buffer[6] = (size >> 16) & 0xff; buffer[7] = (size >> 24) & 0xff;
  buffer[8] = 0x57; buffer[9] = 0x41; buffer[10] = 0x56; buffer[11] = 0x45; // WAVE
  buffer[12] = 0x66; buffer[13] = 0x6d; buffer[14] = 0x74; buffer[15] = 0x20; // fmt
  buffer[16] = 16; buffer[17] = 0; buffer[18] = 0; buffer[19] = 0; // standard chunk size 16
  buffer[20] = 1; buffer[21] = 0; // PCM format 1
  buffer[22] = 1; buffer[23] = 0; // Mono channel
  buffer[24] = sampleRate & 0xff; buffer[25] = (sampleRate >> 8) & 0xff; buffer[26] = (sampleRate >> 16) & 0xff; buffer[27] = (sampleRate >> 24) & 0xff;
  buffer[28] = sampleRate & 0xff; buffer[29] = (sampleRate >> 8) & 0xff; buffer[30] = (sampleRate >> 16) & 0xff; buffer[31] = (sampleRate >> 24) & 0xff;
  buffer[32] = 1; buffer[33] = 0; // block align
  buffer[34] = 8; buffer[35] = 0; // bits per sample
  buffer[36] = 0x64; buffer[37] = 0x61; buffer[38] = 0x74; buffer[39] = 0x61; // data
  buffer[40] = numSamples & 0xff; buffer[41] = (numSamples >> 8) & 0xff; buffer[42] = (numSamples >> 16) & 0xff; buffer[43] = (numSamples >> 24) & 0xff;

  const isEleven = modelId === 'elevenlabs-sound';
  const hz = 180 + (prompt.length % 8) * (isEleven ? 30 : 60);
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const freq = hz + 15 * Math.sin(2 * Math.PI * (isEleven ? 6 : 4) * t); // slight vibrato
    const envelope = Math.exp(-t * (isEleven ? 1.2 : 2.5)); // clean decaying pad
    let wave = Math.sin(2 * Math.PI * freq * t);
    if (isEleven) {
      wave = (wave + 0.5 * Math.sin(2 * Math.PI * freq * 2 * t) + 0.25 * Math.sin(2 * Math.PI * freq * 3 * t)) / 1.75;
    }
    const sample = wave * envelope;
    
    buffer[44 + i] = Math.max(0, Math.min(255, Math.floor((sample + 1.0) * 127.5)));
  }

  let binary = '';
  const len = buffer.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return 'data:audio/wav;base64,' + btoa(binary);
}

function build3DPrism(prompt: string): string {
  const vertices = new Float32Array([
     0.0,  0.8,  0.0,
    -0.6, -0.4, -0.6,
     0.6, -0.4, -0.6,
     0.6, -0.4,  0.6,
    -0.6, -0.4,  0.6
  ]);
  const colors = new Float32Array([
    1.0, 0.3, 0.3,
    0.3, 1.0, 0.3,
    0.3, 0.3, 1.0,
    1.0, 1.0, 0.3,
    1.0, 0.3, 1.0
  ]);
  const indices = new Uint16Array([
    0, 1, 2,
    0, 2, 3,
    0, 3, 4,
    0, 4, 1,
    1, 4, 3,
    1, 3, 2
  ]);

  const parts = [
    new Uint8Array(vertices.buffer),
    new Uint8Array(colors.buffer),
    new Uint8Array(indices.buffer)
  ];
  let total = 0;
  const offsets: number[] = [];
  for (const p of parts) {
    offsets.push(total);
    total += (p.length + 3) & ~3;
  }
  const binBuffer = new Uint8Array(total);
  for (let i = 0; i < parts.length; i++) {
    binBuffer.set(parts[i], offsets[i]);
  }

  const gltf = {
    asset: { version: '2.0', generator: 'glTF-prism-gen' },
    scene: 0,
    scenes: [{ nodes: [0] }],
    nodes: [{ mesh: 0 }],
    meshes: [{
      primitives: [{
        attributes: { POSITION: 0, COLOR_0: 1 },
        indices: 2,
        mode: 4
      }]
    }],
    bufferViews: [
      { buffer: 0, byteOffset: offsets[0], byteLength: vertices.byteLength, target: 34962 },
      { buffer: 0, byteOffset: offsets[1], byteLength: colors.byteLength, target: 34962 },
      { buffer: 0, byteOffset: offsets[2], byteLength: indices.byteLength, target: 34963 }
    ],
    accessors: [
      { bufferView: 0, componentType: 5126, count: 5, type: 'VEC3', min: [-0.6, -0.4, -0.6], max: [0.6, 0.8, 0.6] },
      { bufferView: 1, componentType: 5126, count: 5, type: 'VEC3' },
      { bufferView: 2, componentType: 5123, count: 18, type: 'SCALAR' }
    ],
    buffers: [{ byteLength: binBuffer.byteLength }]
  };

  const jsonStr = JSON.stringify(gltf);
  const jsonBytes = new TextEncoder().encode(jsonStr);
  const jsonAlign = (jsonBytes.length + 3) & ~3;
  const jsonPadded = new Uint8Array(jsonAlign);
  jsonPadded.set(jsonBytes);
  for (let i = jsonBytes.length; i < jsonAlign; i++) jsonPadded[i] = 0x20;

  const binAlign = (binBuffer.length + 3) & ~3;
  const binPadded = new Uint8Array(binAlign);
  binPadded.set(binBuffer);

  const header = new ArrayBuffer(12);
  const dv = new DataView(header);
  dv.setUint32(0, 0x46546C67, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, 12 + 8 + jsonAlign + 8 + binAlign, true);

  const jsonHeader = new ArrayBuffer(8);
  const jh = new DataView(jsonHeader);
  jh.setUint32(0, jsonAlign, true);
  jh.setUint32(4, 0x4E4F534A, true);

  const binHeader = new ArrayBuffer(8);
  const bh = new DataView(binHeader);
  bh.setUint32(0, binAlign, true);
  bh.setUint32(4, 0x004E4942, true);

  const out = new Uint8Array(12 + 8 + jsonAlign + 8 + binAlign);
  let pos = 0;
  out.set(new Uint8Array(header), pos); pos += 12;
  out.set(new Uint8Array(jsonHeader), pos); pos += 8;
  out.set(jsonPadded, pos); pos += jsonAlign;
  out.set(new Uint8Array(binHeader), pos); pos += 8;
  out.set(binPadded, pos);

  let binary = '';
  for (let i = 0; i < out.byteLength; i++) {
    binary += String.fromCharCode(out[i]);
  }
  return 'data:model/gltf-binary;base64,' + btoa(binary);
}